/**
 * weekdaySalesMockData.ts — Datos demo de ventas por día de la semana.
 *
 * QUÉ HACE: genera, de forma DETERMINISTA (sin Math.random), el total vendido
 *   por cada día de la semana para los periodos Semana/Mes/Año, con un reparto
 *   Caja/Web fijo. Sirve de respaldo cuando el negocio aún no tiene actividad
 *   real de caja (modo demo).
 *
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React.
 *
 * Exporta: WEEKDAY_SALES_MOCK_DATA
 */
import type { WeekdayPeriod, WeekdaySalesPoint } from "../types/metrics.types";

const WEEKDAY_NAMES = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];
const WEEKDAY_SHORT = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

/** Total base (último mes) por día, en orden Lunes → Domingo. */
const BASE_MONTH_TOTAL = [
  12_000_000, 14_000_000, 13_000_000, 17_000_000, 25_000_000, 31_000_000,
  18_000_000,
];

/** Factor aplicado al total mensual según el periodo activo. */
const PERIOD_FACTOR: Record<WeekdayPeriod, number> = {
  semana: 1 / 4.3,
  mes: 1,
  anio: 12,
};

/** Participación de caja sobre el total (el resto es tienda virtual). */
const CAJA_SHARE = 0.78;

function buildWeekdaySales(period: WeekdayPeriod): WeekdaySalesPoint[] {
  const factor = PERIOD_FACTOR[period];
  return WEEKDAY_NAMES.map((name, index) => {
    const total = Math.round(BASE_MONTH_TOTAL[index] * factor);
    const caja = Math.round(total * CAJA_SHARE);
    const tienda = total - caja;
    return {
      weekday: index,
      name,
      shortName: WEEKDAY_SHORT[index],
      caja,
      tienda,
      total,
    };
  });
}

export const WEEKDAY_SALES_MOCK_DATA: Record<
  WeekdayPeriod,
  WeekdaySalesPoint[]
> = {
  semana: buildWeekdaySales("semana"),
  mes: buildWeekdaySales("mes"),
  anio: buildWeekdaySales("anio"),
};
